// store/forms/delForm/autosaveErrors/delFormAutosaveErrorsActions.js

import { autosaveErrorsInitialState } from "../delFormState";

/**
 * Creates autosave error actions for the DEL form.
 * These actions manage validation and database error state from WebSocket autosave operations.
 *
 * @param {Function} set - Zustand set function
 * @param {Function} get - Zustand get function
 * @param {string} tabKey - The tab key for the current form instance
 * @returns {Object} Frozen object containing error management actions
 */
export const makeAutosaveErrorsActions = (set, get, tabKey) => ({
  /**
   * Set a field-level error from validation failure
   * @param {string} patchType - The patch type (project, employee, unit, etc.)
   * @param {string} fieldName - The field that failed validation
   * @param {string} errorMessage - User-friendly error message
   */
  setFieldError(patchType, fieldName, errorMessage) {
    const key = `${patchType}.${fieldName}`;
    set((state) => ({
      [tabKey]: {
        ...state[tabKey],
        delForm: {
          ...state[tabKey].delForm,
          autosaveErrors: {
            ...state[tabKey].delForm.autosaveErrors,
            hasErrors: true,
            lastError: {
              key,
              message: errorMessage,
              timestamp: Date.now(),
              patchType,
            },
            fieldErrors: {
              ...state[tabKey].delForm.autosaveErrors.fieldErrors,
              [key]: errorMessage,
            },
          },
        },
      },
    }));
  },

  /**
   * Set row-level errors for row-based sections (employees, units, etc.)
   * @param {string} rowId - UUID of the row with errors
   * @param {Object} errors - Object mapping field names to error messages
   * @param {string} patchType - The patch type for context
   */
  setRowError(rowId, errors, patchType) {
    set((state) => ({
      [tabKey]: {
        ...state[tabKey],
        delForm: {
          ...state[tabKey].delForm,
          autosaveErrors: {
            ...state[tabKey].delForm.autosaveErrors,
            hasErrors: true,
            lastError: {
              key: rowId,
              message: Object.values(errors)[0] || "Validation error",
              timestamp: Date.now(),
              patchType,
              rowId,
            },
            rowErrors: {
              ...state[tabKey].delForm.autosaveErrors.rowErrors,
              [rowId]: errors,
            },
          },
        },
      },
    }));
  },

  /**
   * Clear error for a specific field (called on successful save)
   * @param {string} patchType - The patch type
   * @param {string} fieldName - The field to clear
   */
  clearFieldError(patchType, fieldName) {
    const key = `${patchType}.${fieldName}`;
    set((state) => {
      const currentErrors = state[tabKey].delForm.autosaveErrors;
      const newFieldErrors = { ...currentErrors.fieldErrors };
      delete newFieldErrors[key];
      const hasRemainingErrors =
        Object.keys(newFieldErrors).length > 0 ||
        Object.keys(currentErrors.rowErrors).length > 0;
      return {
        [tabKey]: {
          ...state[tabKey],
          delForm: {
            ...state[tabKey].delForm,
            autosaveErrors: {
              ...currentErrors,
              fieldErrors: newFieldErrors,
              hasErrors: hasRemainingErrors,
              lastError: hasRemainingErrors ? currentErrors.lastError : null,
            },
          },
        },
      };
    });
  },

  /**
   * Clear errors for a specific row
   * @param {string} rowId - UUID of the row to clear
   */
  clearRowError(rowId) {
    set((state) => {
      const currentErrors = state[tabKey].delForm.autosaveErrors;
      const newRowErrors = { ...currentErrors.rowErrors };
      delete newRowErrors[rowId];
      const hasRemainingErrors =
        Object.keys(currentErrors.fieldErrors).length > 0 ||
        Object.keys(newRowErrors).length > 0;
      return {
        [tabKey]: {
          ...state[tabKey],
          delForm: {
            ...state[tabKey].delForm,
            autosaveErrors: {
              ...currentErrors,
              rowErrors: newRowErrors,
              hasErrors: hasRemainingErrors,
              lastError: hasRemainingErrors ? currentErrors.lastError : null,
            },
          },
        },
      };
    });
  },

  /**
   * Clear all errors for a specific patch type
   * @param {string} patchType - The patch type to clear (project, employee, etc.)
   */
  clearPatchTypeErrors(patchType) {
    set((state) => {
      const currentErrors = state[tabKey].delForm.autosaveErrors;
      const newFieldErrors = Object.fromEntries(
        Object.entries(currentErrors.fieldErrors).filter(
          ([key]) => !key.startsWith(`${patchType}.`)
        )
      );
      const hasRemainingErrors =
        Object.keys(newFieldErrors).length > 0 ||
        Object.keys(currentErrors.rowErrors).length > 0;
      return {
        [tabKey]: {
          ...state[tabKey],
          delForm: {
            ...state[tabKey].delForm,
            autosaveErrors: {
              ...currentErrors,
              fieldErrors: newFieldErrors,
              hasErrors: hasRemainingErrors,
              lastError: hasRemainingErrors ? currentErrors.lastError : null,
            },
          },
        },
      };
    });
  },

  /**
   * Get error for a specific field
   * @param {string} patchType - The patch type
   * @param {string} fieldName - The field name
   * @returns {string|null} Error message or null
   */
  getFieldError(patchType, fieldName) {
    const key = `${patchType}.${fieldName}`;
    return get()[tabKey]?.delForm?.autosaveErrors?.fieldErrors?.[key] || null;
  },

  /**
   * Get errors for a specific row
   * @param {string} rowId - UUID of the row
   * @returns {Object|null} Error object or null
   */
  getRowError(rowId) {
    return get()[tabKey]?.delForm?.autosaveErrors?.rowErrors?.[rowId] || null;
  },

  /**
   * Get all current errors state
   * @returns {Object} Current autosaveErrors state
   */
  getAutosaveErrors() {
    return (
      get()[tabKey]?.delForm?.autosaveErrors || autosaveErrorsInitialState
    );
  },

  /**
   * Reset all autosave errors (called on form load/navigation)
   */
  resetAutosaveErrors() {
    set((state) => ({
      [tabKey]: {
        ...state[tabKey],
        delForm: {
          ...state[tabKey].delForm,
          autosaveErrors: autosaveErrorsInitialState,
        },
      },
    }));
  },

  /**
   * Process server error response and set appropriate errors
   * @param {Object} errorData - Structured error from server
   */
  handleServerError(errorData) {
    if (!errorData) return;

    const { error_type, patch_type, field_errors, row_id, summary } = errorData;

    if (error_type === "validation" && field_errors) {
      // Set individual field errors
      Object.entries(field_errors).forEach(([field, message]) => {
        const key = `${patch_type}.${field}`;
        set((state) => ({
          [tabKey]: {
            ...state[tabKey],
            delForm: {
              ...state[tabKey].delForm,
              autosaveErrors: {
                ...state[tabKey].delForm.autosaveErrors,
                hasErrors: true,
                lastError: {
                  key,
                  message,
                  timestamp: Date.now(),
                  patchType: patch_type,
                  rowId: row_id,
                  summary,
                },
                fieldErrors: {
                  ...state[tabKey].delForm.autosaveErrors.fieldErrors,
                  [key]: message,
                },
              },
            },
          },
        }));
      });

      // Also set row error if row_id present
      if (row_id && field_errors) {
        set((state) => ({
          [tabKey]: {
            ...state[tabKey],
            delForm: {
              ...state[tabKey].delForm,
              autosaveErrors: {
                ...state[tabKey].delForm.autosaveErrors,
                rowErrors: {
                  ...state[tabKey].delForm.autosaveErrors.rowErrors,
                  [row_id]: field_errors,
                },
              },
            },
          },
        }));
      }
    } else if (error_type === "database") {
      // Database errors don't have field-level detail
      set((state) => ({
        [tabKey]: {
          ...state[tabKey],
          delForm: {
            ...state[tabKey].delForm,
            autosaveErrors: {
              ...state[tabKey].delForm.autosaveErrors,
              hasErrors: true,
              lastError: {
                key: "database",
                message: summary || "Database error",
                timestamp: Date.now(),
                patchType: patch_type,
                rowId: row_id,
                summary,
              },
            },
          },
        },
      }));
    }
  },
});
